import React, { useState, Suspense, lazy, useEffect, useCallback } from 'react';
import { LandingPage } from './components/landing/LandingPage';
import { TradesLandingPage } from './components/landing/TradesLandingPage';
import { WhatSoftwareLandingPage } from './components/landing/WhatSoftwareLandingPage';
import { ToolsNeededLandingPage } from './components/landing/ToolsNeededLandingPage';
import { SmallBusinessSoftwareLandingPage } from './components/landing/SmallBusinessSoftwareLandingPage';
import { Questionnaire } from './components/Questionnaire';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PWAPrompt } from './components/PWAPrompt';
import { ScrollToTop } from './components/ScrollToTop';
import { LoadingState } from './components/recommendations/LoadingState';
import type { QuestionnaireData } from './types';
import { getRecommendations } from './utils/recommendations';
import { useLocation, Route, Switch } from 'wouter';
import { logger } from './utils/logger';
import { QuestionnaireSessionService, SessionSaveError } from './services/questionnaireSession';
import { ToolDetailPage } from './components/tools/ToolDetailPage';
import { CategoryPage } from './components/categories/CategoryPage';
import { AuthGuard } from './components/auth/AuthGuard';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { PricingPage } from './pages/PricingPage';
import { SuccessPage } from './pages/SuccessPage';
import { PaywallPage } from './pages/PaywallPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { DirectoryIndexPage } from './pages/DirectoryIndexPage';
import { CityDirectoryPage } from './pages/CityDirectoryPage';
import { ProductCard } from './components/stripe/ProductCard';
import { SubscriptionStatus } from './components/stripe/SubscriptionStatus';
import { STRIPE_PRODUCTS } from './stripe-config';
import { supabase } from './lib/supabase';
import { LogOut, User } from 'lucide-react';

// Lazy-loaded components
const Recommendations = lazy(() => import('./components/Recommendations'));
const ToolDirectory = lazy(() => import('./components/directory/ToolDirectory'));

interface SaveError {
  message: string;
  isRetryable: boolean;
}

export default function App() {
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [pendingData, setPendingData] = useState<QuestionnaireData | null>(null);
  const [location, setLocation] = useLocation();
  
  useEffect(() => {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      logger.info('Clearing service worker cache to prevent routing issues');
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_SESSION_CACHE'
      });
    }

    if (navigator.serviceWorker) {
      navigator.serviceWorker.ready.then(registration => {
        registration.update().catch(error => {
          logger.error('Error updating service worker:', error);
        });
      });
    }
  }, []);

  useEffect(() => {
    if (location !== '/questionnaire' && isGenerating) {
      setIsGenerating(false);
    }
  }, [location, isGenerating]);


  const saveAndRedirect = useCallback(async (data: QuestionnaireData) => {
    setIsSaving(true);
    setSaveError(null);
    setPendingData(data);

    try {
      logger.info('Questionnaire completed, saving session to database');

      await QuestionnaireSessionService.saveSessionMandatory({
        formData: data,
        currentStep: 8,
      });

      logger.info('Session saved successfully, redirecting to unlock page');
      setIsSaving(false);
      setPendingData(null);
      setLocation('/unlock');
    } catch (error) {
      logger.error('Failed to save session', error);
      setIsSaving(false);

      if (error instanceof SessionSaveError) {
        setSaveError({
          message: error.message,
          isRetryable: error.isRetryable,
        });
      } else {
        setSaveError({
          message: 'An unexpected error occurred. Please try again.',
          isRetryable: true,
        });
      }
    }
  }, [setLocation]);

  const handleComplete = async (data: QuestionnaireData) => {
    await saveAndRedirect(data);
  };

  const handleRetryAfterSaveError = useCallback(() => {
    if (pendingData) {
      saveAndRedirect(pendingData);
    }
  }, [pendingData, saveAndRedirect]);

  // Handle navigating back to the home page
  const handleStartOver = () => {
    setRecommendations(null);
    setShowQuestionnaire(false);
    setLocation('/');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <ErrorBoundary>
      <PWAPrompt />
      <ScrollToTop />
      <Switch>
        {/* Auth Pages */}
        <Route path="/login" component={LoginPage} />
        <Route path="/signup" component={SignupPage} />
        <Route path="/pricing" component={PricingPage} />
        <Route path="/success" component={SuccessPage} />
        <Route path="/unlock" component={PaywallPage} />

        {/* Dashboard - Protected Route */}
        <Route path="/dashboard">
          <AuthGuard>
            <div className="min-h-screen bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                  <h1 className="text-3xl font-bold text-gray-900">btoolme</h1>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>

                {/* Subscription Status */}
                <div className="mb-8">
                  <SubscriptionStatus />
                </div>

                {/* Products */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Products</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {STRIPE_PRODUCTS.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </AuthGuard>
        </Route>

        {/* Tool Detail Pages */}
        <Route path="/tools/:toolSlug">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <Suspense fallback={<LoadingState />}>
              <ToolDetailPage />
            </Suspense>
          </div>
        </Route>

        {/* Category Pages */}
        <Route path="/categories/:categorySlug">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <Suspense fallback={<LoadingState />}>
              <CategoryPage />
            </Suspense>
          </div>
        </Route>

        {/* Tool Directory - Browse All Tools */}
        <Route path="/tools">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <Suspense fallback={<LoadingState />}>
              <ToolDirectory />
            </Suspense>
          </div>
        </Route>

        {/* City Directory Page */}
        <Route path="/directory/:citySlug">
          <Suspense fallback={<LoadingState />}>
            <CityDirectoryPage />
          </Suspense>
        </Route>

        {/* Directory Index Page */}
        <Route path="/directory">
          <Suspense fallback={<LoadingState />}>
            <DirectoryIndexPage />
          </Suspense>
        </Route>

        {/* Questionnaire Page */}
        <Route path="/questionnaire">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="container mx-auto py-12">
              {isSaving ? (
                <div className="max-w-lg mx-auto px-4 text-center">
                  <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Saving Your Progress</h2>
                    <p className="text-gray-600">Please wait while we securely save your questionnaire data...</p>
                  </div>
                </div>
              ) : saveError ? (
                <div className="max-w-lg mx-auto px-4 text-center">
                  <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Save</h2>
                    <p className="text-gray-600 mb-6">{saveError.message}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      {saveError.isRetryable && (
                        <button
                          onClick={handleRetryAfterSaveError}
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Try Again
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSaveError(null);
                          setPendingData(null);
                        }}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Go Back
                      </button>
                    </div>
                  </div>
                </div>
              ) : isGenerating ? (
                <LoadingState />
              ) : !recommendations ? (
                <Questionnaire onComplete={handleComplete} />
              ) : (
                <Suspense fallback={<LoadingState />}>
                  <Recommendations
                    recommendations={recommendations}
                    questionnaireData={pendingData}
                    userEmail={userEmail}
                    userName={userName}
                    onStartOver={handleStartOver}
                  />
                </Suspense>
              )}
            </div>
          </div>
        </Route>

        {/* Results Pages */}
        <Route path="/results/:businessType">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="container mx-auto py-12">
              {isGenerating ? (
                <LoadingState />
              ) : recommendations ? (
                <Suspense fallback={<LoadingState />}>
                  <Recommendations
                    recommendations={recommendations}
                    questionnaireData={pendingData}
                    userEmail={userEmail}
                    userName={userName}
                    onStartOver={handleStartOver}
                  />
                </Suspense>
              ) : (
                <LandingPage onGetStarted={() => {
                  setShowQuestionnaire(true);
                  setLocation('/questionnaire');
                }} />
              )}
            </div>
          </div>
        </Route>

        {/* What Software Landing Page */}
        <Route path="/what-software-do-i-need-for-my-business">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <WhatSoftwareLandingPage />
          </div>
        </Route>

        {/* Tools Needed Landing Page */}
        <Route path="/tools-needed-to-start-a-business">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <ToolsNeededLandingPage />
          </div>
        </Route>

        {/* Small Business Software Landing Page */}
        <Route path="/what-software-should-a-small-business-use">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <SmallBusinessSoftwareLandingPage />
          </div>
        </Route>

        {/* Trades Landing Page */}
        <Route path="/for-trades">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <TradesLandingPage />
          </div>
        </Route>

        {/* Home Page */}
        <Route path="/">
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <LandingPage onGetStarted={() => {
              setShowQuestionnaire(true);
              setLocation('/questionnaire');
            }} />
          </div>
        </Route>

        {/* 404 Page - Catch-all route */}
        <Route>
          <NotFoundPage />
        </Route>
      </Switch>
    </ErrorBoundary>
  );
}